// ICSP-43: pre-chat adult self-certification gate.
//
// The underage block used to live only inside the intake conversation,
// where a user could recant ("era uno scherzo, ho 35 anni") and self-
// unlock. This gate moves the certification outside the LLM's discretion:
// before the chat mounts, any user whose account hasn't recorded an adult
// declaration (legacy accounts created before the signup checkbox) must
// tick an explicit box. New signups already declared at registration and
// pass straight through.
//
// While /me is loading we render nothing (a blank paper surface) rather
// than flashing the gate at users who have already declared.

import { useEffect, useState, type ReactNode } from "react";

import { Button, MimirMark, Surface } from "@/design-system";
import { useDeclareAdultMutation, useMeQuery } from "@/api/queries";
import { useSetAdultGate } from "@/lib/adultGateContext";

export interface AdultGateProps {
  children: ReactNode;
}

export function AdultGate({ children }: AdultGateProps) {
  const me = useMeQuery();
  const declare = useDeclareAdultMutation();
  const [checked, setChecked] = useState(false);

  // ICSP-59: hide the bottom nav while the user isn't through the gate —
  // both during the /me load (blank surface) and the interstitial itself.
  // This is presentation-only; it does not change any gate decision below.
  const setGated = useSetAdultGate();
  const gating = me.isPending || (!me.data?.adult_declared && !me.isError);
  useEffect(() => {
    setGated(gating);
    return () => setGated(false);
  }, [gating, setGated]);

  // Until we know the user's status, render a blank surface — NOT the chat.
  // Chat creates a session on mount, so mounting it before the gate
  // decision would leak an orphan session for legacy users who then get
  // gated. /me is usually already cached (login seeds it), so this is a
  // no-flash path in the common case.
  if (me.isPending) {
    return <Surface scroll={false}>{null}</Surface>;
  }

  // Declared (signup checkbox or a prior pass through this gate) → chat.
  if (me.data?.adult_declared) {
    return <>{children}</>;
  }

  // /me failed to load — let the chat (and its own auth handling) take over
  // rather than trapping the user behind a gate we can't evaluate.
  if (me.isError) {
    return <>{children}</>;
  }

  return (
    <Surface>
      <div className="mx-auto flex h-full max-w-md flex-col items-center justify-center px-6 text-center">
        <MimirMark />
        <h1 className="mt-6 text-xl font-semibold text-ink">
          Un attimo prima di iniziare
        </h1>
        <p className="mt-3 text-[14px] leading-[1.6] text-ink-muted">
          InCorpoSano è riservato ai maggiorenni. Per i minori è necessario
          rivolgersi al proprio medico o pediatra.
        </p>
        <label className="mt-6 flex cursor-pointer items-start gap-3 text-left">
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            className="mt-0.5 h-4 w-4 accent-rame"
          />
          <span className="text-[13px] leading-[1.5] text-ink-muted">
            Dichiaro di avere almeno 18 anni.
          </span>
        </label>
        {declare.isError && (
          <p className="mt-3 text-[12.5px] text-brick-danger">
            Qualcosa è andato storto. Riprova fra qualche istante.
          </p>
        )}
        <Button
          variant="primary"
          full
          className="mt-6"
          disabled={!checked || declare.isPending}
          onClick={() => declare.mutate()}
        >
          {declare.isPending ? "Un istante…" : "Continua"}
        </Button>
      </div>
    </Surface>
  );
}
